import { interpolate, plural, type PluralForms } from './interpolate.ts';
import { type Dictionary, ru } from './ru.ts';

export { interpolate, plural };

export type Join<P extends string, K extends string> = P extends '' ? K : `${P}.${K}`;

/** Все ключи-листья словаря: 'common.save', 'errors.mail.address_taken', … (без узлов склонений). */
export type MessageKeys<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? Join<P, K>
    : T[K] extends PluralForms
      ? never
      : MessageKeys<T[K], Join<P, K>>;
}[keyof T & string];

/** Ключи узлов склонений: 'units.hours', … */
export type PluralKeys<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? never
    : T[K] extends PluralForms
      ? Join<P, K>
      : PluralKeys<T[K], Join<P, K>>;
}[keyof T & string];

export type ValueAt<T, K extends string> = K extends `${infer H}.${infer R}`
  ? H extends keyof T
    ? ValueAt<T[H], R>
    : never
  : K extends keyof T
    ? T[K]
    : never;

type Placeholders<S> = S extends `${string}{${infer P}}${infer R}` ? P | Placeholders<R> : never;

export type MessageParams<S> = Readonly<Record<Placeholders<S>, string | number>>;
export type ParamsArgs<S> = [Placeholders<S>] extends [never] ? [] : [params: MessageParams<S>];

export type MessageKey = MessageKeys<Dictionary>;
export type PluralKey = PluralKeys<Dictionary>;
export type ErrorCode = MessageKeys<Dictionary['errors']>;

function lookup(path: string): unknown {
  let node: unknown = ru;
  for (const part of path.split('.')) {
    if (typeof node !== 'object' || node === null || !Object.hasOwn(node, part)) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

export function t<K extends MessageKey>(key: K, ...args: ParamsArgs<ValueAt<Dictionary, K>>): string {
  const message = lookup(key);
  if (typeof message !== 'string') throw new Error(`Нет строки словаря: ${key}`);
  return interpolate(message, args[0]);
}

/**
 * Строка по ключу, пришедшему из данных (объявления типов, реестры): ключ проверен типом,
 * параметры — нет. Для обычного кода — `t()`.
 */
export function tDynamic(key: MessageKey, params: Readonly<Record<string, string | number>> = {}): string {
  const message = lookup(key);
  if (typeof message !== 'string') throw new Error(`Нет строки словаря: ${key}`);
  return interpolate(message, params);
}

/**
 * Строка по ключу, собранному из данных (действие журнала, тип объекта), или null, если такой
 * строки нет: вызывающий показывает исходное значение.
 */
export function tOptional(
  key: string,
  params: Readonly<Record<string, string | number>> = {},
): string | null {
  const message = lookup(key);
  return typeof message === 'string' ? interpolate(message, params) : null;
}

export function tp(key: PluralKey, count: number): string {
  return plural(lookup(key) as PluralForms, count);
}

export function isErrorCode(code: string): code is ErrorCode {
  return typeof lookup(`errors.${code}`) === 'string';
}

/** Русское сообщение для кода доменной ошибки. Неизвестный код — «Что-то пошло не так». */
export function errorMessage(
  code: ErrorCode,
  params: Readonly<Record<string, string | number>> = {},
): string {
  const message = lookup(`errors.${code}`);
  if (typeof message !== 'string') return interpolate(ru.errors.common.unexpected, params);
  return interpolate(message, params);
}

export type AuthErrorCode = keyof Dictionary['authErrors'];

/**
 * Русское сообщение для ошибки Better Auth (вход, 2FA, passkey). Коды платформы
 * (`identity.account_deactivated` из проверок входа) берутся из словаря ошибок.
 */
export function authErrorMessage(error: {
  readonly code?: string | undefined;
  readonly status?: number | undefined;
}): string {
  const { code, status } = error;
  if (code !== undefined && Object.hasOwn(ru.authErrors, code)) return ru.authErrors[code as AuthErrorCode];
  if (code !== undefined && isErrorCode(code)) return errorMessage(code);
  if (status === 429) return ru.authErrors.TOO_MANY_REQUESTS;
  return ru.authErrors.UNKNOWN;
}
